import * as React from 'react';

import {Popover} from 'shells/popover/popover';
import {Body} from 'shells/typography/body';

import type {GetProductsForLead_lead_products_edges as LeadProductMap} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../../refetch-helpers';

import {useUpdateProductForLead} from '../../revenue-widget/graphql/hooks/use-update-product-for-lead';

import {formatWithSymbol, parseMoneyString} from '../helpers';

import {FooterButtons} from './footer-buttons';
import {ProductData} from './product-data';
import {ValueFields} from './value-fields';

type Props = {
    anchor: HTMLElement;
    product: LeadProductMap;
    onClose: () => void;
};

export function ProductPopover(props: Props) {
    const [quantity, setQuantity] = React.useState<string>(String(props.product.quantity));
    const [price, setPrice] = React.useState<string>(props.product.price.formattedWithCents);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const originalUnitPrice = React.useMemo<number | null>(() => {
        if (props.product.originalPrice) {
            return props.product.originalPrice.amount;
        }

        return null;
    }, [props.product]);

    const [discountPercentage, setDiscountPercentage] = React.useState<string>(() => {
        if (!originalUnitPrice || originalUnitPrice === 0) {
            return '0';
        }

        const current = parseMoneyString(props.product.price.formattedWithCents);
        const pct = Math.round((1 - current / originalUnitPrice) * 100);

        return String(isNaN(pct) ? 0 : pct);
    });

    const {updateProductForLead} = useUpdateProductForLead();

    const parsedQuantity = Math.max(0, parseInt(quantity || '0', 10) || 0);
    const parsedPrice = parseMoneyString(price);
    const total = isNaN(parsedPrice) ? 0 : parsedPrice * parsedQuantity;

    const handleChangePrice = (val: string) => {
        setPrice(val);
        if (originalUnitPrice && originalUnitPrice !== 0) {
            const p = parseMoneyString(val);
            if (!isNaN(p)) {
                const pct = Math.round((1 - p / originalUnitPrice) * 100);
                setDiscountPercentage(String(pct));
            }
        }
    };

    const handleChangeDiscount = (val: string) => {
        setDiscountPercentage(val);
        if (originalUnitPrice) {
            const pct = parseMoneyString(val);
            if (!isNaN(pct)) {
                const newPrice = originalUnitPrice * (1 - pct / 100);
                setPrice(formatWithSymbol(newPrice, currencySymbol));
            }
        }
    };

    const handleSave = () => {
        setIsSaving(true);
        setError(null);
        updateProductForLead({
            leadProductMap: props.product.id,
            quantity: parsedQuantity,
            price: parsedPrice.toFixed(2),
        })
            .then(() => {
                props.onClose();
                refetchForEvent('productsUpdated');
            })
            .catch((e: Error) => {
                setError(e && e.message ? e.message : 'Failed to save');
            })
            .finally(() => setIsSaving(false));
    };

    const currencySymbol = props.product.price.symbol;

    return (
        <Popover
            anchor={props.anchor}
            onBlur={props.onClose}
            disableFocusTrapZone={true}
            customStyles={{
                width: '385px',
            }}
        >
            <div className='pad-16 flex flex-dir-col gap-16'>
                <ProductData
                    product={props.product}
                    currencySymbol={currencySymbol}
                    originalUnitPrice={originalUnitPrice}
                />
                <ValueFields
                    quantity={quantity}
                    price={price}
                    discountPercentage={discountPercentage}
                    total={total}
                    currencySymbol={currencySymbol}
                    onChangeQuantity={setQuantity}
                    onChangePrice={handleChangePrice}
                    onChangeDiscount={handleChangeDiscount}
                />
                {error && (
                    <Body size='small' color='error'>
                        {error}
                    </Body>
                )}
                <FooterButtons
                    onClose={props.onClose}
                    isSaving={isSaving}
                    handleSave={handleSave}
                />
            </div>
        </Popover>
    );
}
